import {useState} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {Button} from '@/components';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import type {AfriexCountry} from '@/types';
import {CountryStatus} from '@/types';

import AvailabilitySettingsModal from './AvailabilitySettingsModal';
import {
  COUNTRY_AVAILABILITY_ENFORCEMENT_NOTE,
  COUNTRY_AVAILABILITY_PERMISSIONS,
  resolvePermission,
} from './countryAvailability';

type AvailabilitySettingsProps = {
  handleSave: (id: string, body: Partial<AfriexCountry>) => Promise<void>;
  item: AfriexCountry;
};

/**
 * Settings -> Country -> Availability.
 *
 * `status` is the only field that removes a corridor from the retail app
 * rather than failing it at submit time: `GET /v2/countries` defaults to
 * `status=active`, and the app's currency pickers filter what it returns to
 * active countries. Until now it was readable on this screen and editable
 * only in Mongo, which is why closing a corridor meant either a database edit
 * or a currency kill switch that let the customer get all the way to the
 * confirmation screen before failing.
 *
 * The currency kill switches (Settings -> Currency) are the other half and
 * still matter: they stop the money. This tab stops the corridor being
 * offered. Closing a corridor properly means both.
 */
const AvailabilitySettings = ({
  item,
  handleSave,
}: AvailabilitySettingsProps) => {
  const [showModal, setShowModal] = useState(false);
  const permissions = item?.permissions;

  return (
    <>
      <div className="flex justify-end mb-6">
        <PermissionsProvider permission={AfriexAdminPermission.CountryUpdate}>
          <Button variant="solid" onClick={() => setShowModal(true)}>
            Update Availability
          </Button>
        </PermissionsProvider>
      </div>

      <div className="flex flex-col gap-3">
        <div className="overflow-x-auto">
          <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
            <p className="text-slate-500 dark:text-slate-100 text-sm">Status</p>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-100 w-1/2">
              {item?.status === CountryStatus.INACTIVE
                ? 'Inactive (hidden from the app)'
                : 'Active'}
            </p>
          </div>

          <p className="mb-3 px-3 text-xs text-slate-500 dark:text-slate-400">
            {COUNTRY_AVAILABILITY_ENFORCEMENT_NOTE}
          </p>

          {COUNTRY_AVAILABILITY_PERMISSIONS.map(
            ({key, label, defaultWhenUnset}) => {
              const stored = permissions?.[key];
              const effective = resolvePermission(
                permissions,
                key,
                defaultWhenUnset,
              );
              return (
                <div
                  key={key}
                  className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between"
                >
                  <p className="text-slate-500 dark:text-slate-100 text-sm">
                    {label}
                  </p>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-100 w-1/2">
                    {/*
                      An unset field is shown as unset AND as what it resolves
                      to, never as a bare Yes/No: the two permissions default
                      opposite ways, so "Not set" alone would leave the reader
                      guessing which one this is.
                    */}
                    {effective ? 'Yes' : 'No'}
                    {stored === undefined || stored === null
                      ? ' (not set)'
                      : ''}
                  </p>
                </div>
              );
            },
          )}
        </div>
      </div>

      <AvailabilitySettingsModal
        onClose={() => setShowModal(false)}
        showModal={showModal}
        handleSave={handleSave}
        item={item}
      />
    </>
  );
};

export default AvailabilitySettings;
